import { type ReactElement, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { type SubscriptionId, SubscriptionIdSchema } from '../../../api';
import type { ApiError } from '../../../lib/api/api-client';
import {
  ChannelSettingsForm,
  limitsAccountFromMe,
  type SavedSettings,
  useSettingsData,
} from '../../settings';
import { useChannels } from '../channel-selection/use-channels';
import { ONBOARDING_ROUTES } from '../routes';

import { type FailureAction, FirstSettingsScreen } from './FirstSettingsScreen';
import { type FirstChannelPick, pickFirstChannel } from './pick-first-channel';

/** E5 leaves for Home, replacing itself in the history (S7 R10, R11; Proposal, to be validated). */
const HOME = '/';

/** The action a failed read offers: sign in again after a lost session, else read again (R15). */
function actionFor(error: ApiError): FailureAction {
  return error.reasonCode === 'TELEGRAM_SESSION_LOST' ? 'signIn' : 'retry';
}

/**
 * Route element of E5 (specs/S7-first-settings/spec.md; provisional on Q21): reads the channels,
 * picks the one to set up (R3), then reads its settings and `GET /me` and shows M2's form in its
 * `firstSetup` variant. "Confirm and start copying" is the form's save; "Set up later" sends
 * nothing (R11). With nothing left to set up it goes straight to Home (R12).
 */
export function FirstSettingsRoute(): ReactElement {
  const navigate = useNavigate();
  const channels = useChannels();
  const pick = channels.data === undefined ? null : pickFirstChannel(channels.data.channels);
  const leave = (): void => {
    void navigate(HOME, { replace: true });
  };
  const signInAgain = (): void => {
    void navigate(ONBOARDING_ROUTES.telegram);
  };

  if (channels.data !== undefined && pick === null) {
    // R12: no channel is left to set up (none selected, or all already set up or inactive).
    return <Navigate to={HOME} replace />;
  }
  if (pick === null) {
    // `GET /channels` is pending, or failed with nothing to show yet.
    return (
      <FirstSettingsScreen
        pick={null}
        loading={channels.status === 'pending'}
        failure={
          channels.status === 'error'
            ? { reason: channels.error, action: actionFor(channels.error) }
            : null
        }
        form={null}
        setUp={false}
        busy={false}
        onRetry={channels.refetch}
        onSignInAgain={signInAgain}
        onLeave={leave}
      />
    );
  }
  // Keyed by subscription: a new pick (after a 404 reload, R15) starts from its own settings,
  // never the previous channel's draft (I7).
  const subscriptionId = pick.channel.subscription?.id;
  const parsed = SubscriptionIdSchema.safeParse(subscriptionId);
  if (!parsed.success) return <Navigate to={HOME} replace />;
  return (
    <PickedChannel
      key={parsed.data}
      subscriptionId={parsed.data}
      pick={pick}
      reloadChannels={channels.refetch}
      onLeave={leave}
      onSignInAgain={signInAgain}
    />
  );
}

/** Props of {@link PickedChannel}. */
interface PickedChannelProps {
  readonly subscriptionId: SubscriptionId;
  readonly pick: FirstChannelPick;
  readonly reloadChannels: () => void;
  readonly onLeave: () => void;
  readonly onSignInAgain: () => void;
}

/** The part of E5 that needs the settings of the picked channel. */
function PickedChannel({
  subscriptionId,
  pick,
  reloadChannels,
  onLeave,
  onSignInAgain,
}: PickedChannelProps): ReactElement {
  const settings = useSettingsData(subscriptionId);
  // R17: a save is running, so "Set up later" is disabled. R13: the channel is set up but the
  // trader must read something (limit already exceeded, limit refused): "Continue to Home".
  const [saving, setSaving] = useState(false);
  const [setUp, setSetUp] = useState(false);
  const gone = settings.status === 'error' && settings.error.reasonCode === 'NOT_FOUND';
  useEffect(() => {
    // R15: the subscription was removed elsewhere, so the list is read again and R3 applies again.
    if (gone) reloadChannels();
  }, [gone, reloadChannels]);
  const { data } = settings;
  if (data !== undefined && data.settings.active !== null) {
    // R12: set up on another device meanwhile (FXM-01 §6 case 8): nothing to do here.
    return <Navigate to={HOME} replace />;
  }
  const onSaved = ({ complete, limits }: SavedSettings): void => {
    if (complete === true && limits?.limitAlreadyReached !== true) onLeave();
    else setSetUp(true);
  };
  return (
    <FirstSettingsScreen
      pick={pick}
      loading={settings.status === 'pending'}
      failure={
        settings.status === 'error'
          ? { reason: settings.error, action: actionFor(settings.error) }
          : null
      }
      form={
        data === undefined ? null : (
          <ChannelSettingsForm
            subscriptionId={subscriptionId}
            active={null}
            activeVersion={null}
            recommended={data.settings.recommended}
            limitsAccount={limitsAccountFromMe(data.me, data.settings.accountLimits)}
            variant="firstSetup"
            onSaved={onSaved}
            onSavingChange={setSaving}
          />
        )
      }
      setUp={setUp}
      busy={saving}
      onRetry={settings.refetch}
      onSignInAgain={onSignInAgain}
      onLeave={onLeave}
    />
  );
}
